import { useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { addGameSearchParams } from '../helpers/gameQueriesManager';
import { detectDevice, envStrToBoolean, hasMissingLimits } from '../helpers/utils';
import { seacrhByGameNameRequest } from '../services/gameApi';
import { openDialog } from '../store/dialog/actions';
import { EnumDialogsKeys, EnumStatusesOfKyc } from '../store/dialog/types';
import { RootState } from '../types/store-types';
import { useConnectedAction } from './use-connected-action';
import { useBannerActions } from './useBannerActions';

type UseGameActionReturn = {
  handleActionGame: (param: number) => void;
  shouldShowKyc: boolean;
  shouldShowLimits: boolean;
};

export const useGameAction = (): UseGameActionReturn => {
  const { user, kycStatus, totalActivityLimitValue, totalBetLimitValue, totalLossLimitValue, totalDepositValue } =
    useSelector((state: RootState) => state.user);

  const { gameAction } = useBannerActions();

  const device = detectDevice();
  const _openDialog = useConnectedAction(openDialog);
  const [searchParams, setQueryParams] = useSearchParams();

  const isLimitsEnabled = envStrToBoolean(process.env.REACT_APP_LIMITS_ENABLE);
  const isKycGameEnable = envStrToBoolean(process.env.REACT_APP_KYC_OTHER_ENABLE);

  const shouldShowKyc = Boolean(
    user &&
      isKycGameEnable &&
      [EnumStatusesOfKyc.PENDING, EnumStatusesOfKyc.EXPIRED, EnumStatusesOfKyc.REJECTED, undefined].includes(
        kycStatus as EnumStatusesOfKyc | undefined
      )
  );

  const shouldShowLimits = Boolean(
    user &&
      isLimitsEnabled &&
      hasMissingLimits(totalActivityLimitValue, totalBetLimitValue, totalLossLimitValue, totalDepositValue)
  );

  const handleActionGame = useCallback(
    (param: number): void => {
      if (shouldShowKyc) {
        _openDialog({
          dialogType: EnumDialogsKeys.KYC_POPUP,
          dialogProps: { data: {} },
        });
      } else if (shouldShowLimits) {
        _openDialog({
          dialogType: EnumDialogsKeys.SET_LIMITS_AFTER_LOGIN_POPUP,
          dialogProps: { data: {} },
        });
      } else {
        if (user) {
          seacrhByGameNameRequest(device, null, null, null, Number(param)).then((gamesInfo) => {
            if (!gamesInfo?.success || !gamesInfo?.games?.length) return;

            const gameInfo = gamesInfo.games[0];
            addGameSearchParams(searchParams, setQueryParams, gameInfo);
          });
        }
        gameAction(param);
      }
    },
    [shouldShowKyc, shouldShowLimits, user, device, searchParams]
  );

  return { handleActionGame, shouldShowKyc, shouldShowLimits };
};
